/**
 * LanguageContext
 *
 * Owns which language the front end is rendering in, and holds paint until
 * i18next has its dictionaries — so no component ever flashes raw translation
 * keys on the way in.
 *
 * Strategy (mirrors AppSettingsContext's cache-then-refresh shape):
 *   1. Resolve a locale synchronously from localStorage + navigator against the
 *      built-in registry, and initialize i18next with it immediately. This path
 *      touches no network, so first paint never waits on an API call.
 *   2. Then fetch the live registry from /api/locales.php and correct course
 *      only if the resolution actually changes — normally it doesn't.
 *
 * Resolution order, highest priority first:
 *   1. A language referral link (/fr-ca, ?lang=fr-CA — see utils/urlLocale.ts),
 *      for this browser session only. It outranks a stored choice for the
 *      visit because it is the freshest signal and the switcher is one click
 *      away, but it never writes to that choice: a link is marketing's guess,
 *      not the visitor's decision, and must not follow them into next month.
 *   2. The visitor's explicit choice (localStorage — survives the session,
 *      unlike the currency override, because language is an accessibility
 *      preference rather than a per-visit pricing context)
 *   3. The retailer portal's default_locale
 *   4. navigator.languages matched against the registry
 *   5. The registry default (first active locale, normally English)
 *
 * Sitting above all five, but only while the content that asked for it is on
 * screen, is applyContentLocale: a saved estimate carries the language it was
 * written in, and its share link renders in that language whatever the reader's
 * own preference. It is borrowed, not chosen — releaseContentLocale hands it
 * back when the estimate goes away, and using the switcher takes ownership.
 *
 * MUST be mounted inside PartnerBrandingProvider (it reads the retailer
 * default) and outside ProductsBootGate (product names are translated
 * server-side in Phase 3, so the locale has to be settled before products load).
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  BUILTIN_LOCALES,
  LOCALE_STORAGE_KEY,
  SOURCE_LOCALE,
  isSupportedLocale,
  matchLocale,
  withPseudoLocale,
  PSEUDO_LOCALE,
  type LocaleDef,
} from '../config/locales';
import { changeLocale, initI18n } from '../i18n';
import { setTrackingContext, trackLanguageChanged } from '../utils/gtmTracking';
import { fetchLocales } from '../services/api';
import { urlLocaleHint, clearUrlLocaleHint } from '../utils/urlLocale';
import { usePartnerBranding } from './PartnerBrandingContext';

interface LanguageContextValue {
  /** Active BCP-47 locale code. */
  locale: string;
  /** Locales this visitor may choose between, already filtered by retailer. */
  locales: LocaleDef[];
  /** Switch language and persist the choice. */
  setLocale: (code: string) => void;
  /**
   * Adopt the language a piece of content was authored in, for this view only.
   *
   * Distinct from setLocale in that it does not persist and does not count as
   * the visitor's own choice — the language is borrowed for as long as that
   * content is on screen, and handed back by releaseContentLocale. It DOES
   * outrank a stored choice: a French estimate opens in French even on a device
   * whose owner usually reads English, because the switcher makes that trivial
   * to undo while silently serving the wrong language does not.
   *
   * No-ops when the code is unknown, unavailable, or already active.
   */
  applyContentLocale: (code: string | null | undefined) => void;
  /**
   * Give back a language borrowed by applyContentLocale.
   *
   * Called when the content that owned it goes away. Without this, opening one
   * French estimate would leave the whole session in French — which is merely
   * odd for a recipient, and actively confusing for an admin paging through
   * other people's estimates. Yields to a switcher choice made in between.
   */
  releaseContentLocale: () => void;
  /** True until i18next is initialized and its first dictionaries are loaded. */
  isLoading: boolean;
  /** True once the visitor has made an explicit choice this device. */
  hasExplicitChoice: boolean;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

// ---------------------------------------------------------------------------
// Storage
// ---------------------------------------------------------------------------

function readStoredLocale(): string | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    return window.localStorage.getItem(LOCALE_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredLocale(code: string): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, code);
  } catch {
    // Quota / private mode — the session still works, the choice just won't stick.
  }
}

/** Browser-preferred tags, most-preferred first. */
function browserLanguages(): string[] {
  if (typeof navigator === 'undefined') return [];
  const list = navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language];
  return list.filter(Boolean);
}

/**
 * Full resolution against a given registry. `stored` is passed in rather than
 * read here so the caller controls whether an explicit choice participates.
 *
 * The referral hint is matched loosely on purpose — a link written as /fr or
 * /fr-fr should land on fr-CA, not on English. It is re-checked against every
 * registry the caller has, so a language only the database knows about still
 * gets its chance once the live list arrives.
 */
function resolveLocale(
  stored: string | null,
  retailerDefault: string | null,
  registry: LocaleDef[],
  registryDefault: string,
): string {
  const hint = urlLocaleHint();
  const fromLink = hint ? matchLocale([hint], registry) : null;
  if (fromLink) return fromLink;

  if (isSupportedLocale(stored, registry)) return stored as string;
  if (isSupportedLocale(retailerDefault, registry)) return retailerDefault as string;

  const matched = matchLocale(browserLanguages(), registry);
  if (matched) return matched;

  return isSupportedLocale(registryDefault, registry) ? registryDefault : SOURCE_LOCALE;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { partner } = usePartnerBranding();
  const retailerDefault = partner?.defaultLocale ?? null;

  const [registry, setRegistry] = useState<LocaleDef[]>(() => withPseudoLocale(BUILTIN_LOCALES));
  const [locale, setLocaleState] = useState<string>(() =>
    resolveLocale(readStoredLocale(), null, withPseudoLocale(BUILTIN_LOCALES), SOURCE_LOCALE),
  );
  const [isLoading, setIsLoading] = useState(true);
  const [hasExplicitChoice, setHasExplicitChoice] = useState(() =>
    isSupportedLocale(readStoredLocale(), withPseudoLocale(BUILTIN_LOCALES)),
  );

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  /**
   * A language borrowed from a piece of content, and what was active before it.
   * A ref rather than state: nothing renders from it, and it must not be a
   * dependency of the callbacks that read it.
   */
  const borrowedRef = useRef<{ applied: string; previous: string } | null>(null);

  /**
   * True once the visitor has used the switcher during this page load.
   *
   * Exists because a language change remounts everything below
   * ProductsBootGate (product names are translated server-side, so the
   * catalogue re-hydrates). A shared estimate therefore refetches on every
   * switch and asks for its own language again — and without this flag it
   * got it, snapping the page straight back and making the switcher useless
   * while reading an estimate. A page load is the right lifetime: a fresh
   * estimate link still opens in its own language, but within a visit the
   * reader's click is final.
   */
  const ownedRef = useRef(false);

  /**
   * The locale i18next was initialized with. Held in a ref so the mount-only
   * effect below reads it without taking a dependency on `locale` — the effect
   * must not re-run when the language changes, because later switches go
   * through changeLocale(), not a re-init.
   */
  const initialLocaleRef = useRef(locale);

  /**
   * Mirrors the current locale for effects that need to compare against it
   * without re-subscribing every time it changes.
   */
  const localeRef = useRef(locale);
  useEffect(() => { localeRef.current = locale; }, [locale]);

  // 1. Initialize i18next immediately with the locally-resolved locale.
  //    No network dependency, so first paint isn't gated on the API.
  useEffect(() => {
    let cancelled = false;
    initI18n(initialLocaleRef.current, withPseudoLocale(BUILTIN_LOCALES))
      .catch((err) => {
        // A failed dictionary load must not white-screen the app — i18next
        // returns keys' fallback text and the UI stays usable in English.
        console.warn('[Language] i18n init failed:', err);
      })
      .finally(() => {
        if (!cancelled && mountedRef.current) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // 2. Fetch the live registry, then re-resolve. Only corrects course when the
  //    server knows something the built-in list didn't (a new language, a
  //    retailer default, a locale an admin deactivated). Re-runs when the
  //    partner resolves, since that's when a retailer default first exists.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const payload = await fetchLocales();
      if (cancelled || !mountedRef.current || !payload) return;

      setRegistry(withPseudoLocale(payload.locales));

      const stored = readStoredLocale();
      const withPseudo = withPseudoLocale(payload.locales);
      const resolved = resolveLocale(stored, retailerDefault, withPseudo, payload.default);
      // Boot resolution is not a user action — context only, no event.
      setTrackingContext({ locale: resolved });
      setHasExplicitChoice(isSupportedLocale(stored, withPseudo));
      if (resolved !== localeRef.current) {
        setLocaleState(resolved);
        await changeLocale(resolved, withPseudo);
      }
    })();
    return () => { cancelled = true; };
  }, [retailerDefault]);

  // Pick up a language change made in another tab.
  useEffect(() => {
    const handler = (event: StorageEvent) => {
      if (event.key !== LOCALE_STORAGE_KEY || !event.newValue) return;
      if (!isSupportedLocale(event.newValue, registry) || event.newValue === localeRef.current) return;
      setTrackingContext({ locale: event.newValue });
      setLocaleState(event.newValue);
      setHasExplicitChoice(true);
      void changeLocale(event.newValue, registry);
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
    // Reads the live locale through the ref so the listener isn't torn down and
    // re-attached on every language switch.
  }, [registry]);

  const setLocale = useCallback((code: string) => {
    if (!isSupportedLocale(code, registry)) {
      console.warn(`[Language] ignoring unsupported locale: ${code}`);
      return;
    }
    // The visitor has taken ownership of the language, so a borrowed content
    // locale is no longer something to hand back on the way out, and a
    // referral link's hint no longer has anything to say.
    borrowedRef.current = null;
    ownedRef.current = true;
    clearUrlLocaleHint();
    // The switcher is the one place a language change is the visitor's own act.
    if (code !== localeRef.current) trackLanguageChanged(localeRef.current, code);
    setTrackingContext({ locale: code });
    setLocaleState(code);
    setHasExplicitChoice(true);
    writeStoredLocale(code);
    void changeLocale(code, registry);
  }, [registry]);

  const applyContentLocale = useCallback((code: string | null | undefined) => {
    if (!code || code === localeRef.current) return;
    if (!isSupportedLocale(code, registry)) return;
    // A switcher click earlier in this page load is final — see ownedRef.
    if (ownedRef.current) return;
    // Deliberately does NOT defer to a stored choice from an earlier visit.
    //
    // The first version did, and it made the feature unobservable: a stored
    // locale is set the moment anyone touches the switcher once, and it never
    // expires, so in practice no estimate link ever opened in its own language.
    //
    // The failure modes are not symmetric, which is what settles it. If the
    // document's language wins and the reader disagrees, they get a page in a
    // language they can see is wrong and the switcher is right there — it
    // corrects itself in one click. If the stored choice wins, a French
    // recipient silently reads English and nothing anywhere tells them the
    // estimate was written in another language.
    borrowedRef.current = { applied: code, previous: localeRef.current };
    setLocaleState(code);
    void changeLocale(code, registry);
  }, [registry]);

  const releaseContentLocale = useCallback(() => {
    const borrowed = borrowedRef.current;
    borrowedRef.current = null;
    // Only give the language back if it is still the one we borrowed. A viewer
    // who used the switcher while reading the estimate has since made it their
    // own, and setLocale has already cleared this.
    if (!borrowed || borrowed.applied !== localeRef.current) return;
    if (!isSupportedLocale(borrowed.previous, registry)) return;
    setLocaleState(borrowed.previous);
    void changeLocale(borrowed.previous, registry);
  }, [registry]);

  /**
   * A retailer may restrict which languages its portal offers, the same way
   * currencies_supported restricts the currency toggle. An empty or absent
   * list means "everything active". The current locale is always kept in the
   * list so the switcher can't render a selection it doesn't contain.
   */
  const available = useMemo(() => {
    const allowed = partner?.localesSupported;
    if (!Array.isArray(allowed) || allowed.length === 0) return registry;
    const filtered = registry.filter(
      l => allowed.includes(l.code) || l.code === locale || l.code === PSEUDO_LOCALE.code,
    );
    return filtered.length > 0 ? filtered : registry;
  }, [registry, partner?.localesSupported, locale]);

  const value = useMemo<LanguageContextValue>(
    () => ({
      locale, locales: available, setLocale,
      applyContentLocale, releaseContentLocale,
      isLoading, hasExplicitChoice,
    }),
    [locale, available, setLocale, applyContentLocale, releaseContentLocale, isLoading, hasExplicitChoice],
  );

  // Hold paint until i18next has initialized and pulled its first dictionaries.
  // Suspense is off (see src/i18n/index.ts), so rendering children early would
  // show raw keys like "wizard:mode.title" for a frame. This gate is short —
  // the locale resolves from localStorage synchronously and the dictionaries
  // are small same-origin JSON — and it fails open: a failed load still clears
  // isLoading, and the UI renders in English via the fallback chain.
  //
  // Deliberately renders nothing rather than a spinner: ProductsBootGate sits
  // directly below and owns the app's loading indicator. Two would flash.
  if (isLoading) return null;

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return ctx;
}
